/**
 * Launcher V2 U2a — the sentinel that replaces "await a dialog" with
 * "publish state, throw synchronously, act later at the origin" (KTD 2, see
 * `usage-sidebar-store.ts`'s module doc).
 *
 * `saveWithGate` (`SequencesStep.tsx`) used to return a Promise that settled
 * once the rep picked a button in an `openAlertDialog`. That Promise cannot
 * survive the unmount `SequencesStep` inflicts on its editor tree (a rail
 * click, `<SequenceEditorShell key={selectedRung.draftKey}>`) or a route
 * change — the dangling-Promise shape is the root cause of a regression
 * (`isSaving`/`isAddingStep` stuck true) shipped twice already.
 *
 * The fix: `saveWithGate`'s `> 1` branch calls `openUsageSidebar(...)` (a
 * plain store write, not awaited) and then throws `new
 * UsageSidebarOpenedError()` SYNCHRONOUSLY, in the same tick — never inside
 * a `.then`/async continuation, since the whole point is that nothing here
 * ever waits on the rep's choice. Every caller up the chain must let this
 * sentinel pass through undecorated until it reaches a TERMINAL catch (the
 * one that would otherwise show a save-failure toast), and swallow it there
 * via {@link isUsageSidebarOpened} — never at an intermediate layer, which
 * would risk hiding a REAL save error thrown for an unrelated reason.
 *
 * A standalone 10-line module (not folded into `usage-sidebar-store.ts` or
 * `use-usage-sidebar-decision.ts`) so both editors (`SequenceStepEditor.tsx`,
 * and `SequenceCopyPane.tsx`'s own gate once U2b lands) can import just the
 * sentinel + guard without pulling in the store or the decision hook — no
 * import cycle through either.
 */
export class UsageSidebarOpenedError extends Error {
  constructor() {
    super('Usage sidebar opened — save/delete deferred to the rep’s decision there.');
    this.name = 'UsageSidebarOpenedError';
  }
}

/**
 * True only for {@link UsageSidebarOpenedError} — ordinary `Error`s, wire
 * error shapes (`{ code: 'shared_mapping_conflict' }`), `null`, and strings
 * all return `false`. Only this sentinel may bypass normal save-error
 * reporting (no toast, saving state cleared, draft left intact).
 */
export function isUsageSidebarOpened(error: unknown): boolean {
  return error instanceof UsageSidebarOpenedError;
}
